import { FIELD_TYPES } from './constants';

// ── ID / slug utils ───────────────────────────────────────────────────────────
export function uid() {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

export function slugify(str) {
  return (str || 'step').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'step';
}

export function slugifyName(str) {
  return (str || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || '';
}

// ── Step factories ────────────────────────────────────────────────────────────
export function makeInitialStep(title = 'Step 1', order = 0) {
  return {
    _id: uid(), _isNew: true, title, order,
    stepType: 'fields',
    fields: [],
    sections: [],
    selectionGroups: [],
    history: [[]], histIdx: 0,
  };
}

export function makeSelectionStep(title = 'Step 1', order = 0) {
  return {
    _id: uid(), _isNew: true, title, order,
    stepType: 'selection',
    fields: [],
    sections: [],
    selectionGroups: [],
    history: [[]], histIdx: 0,
  };
}

// ── Field factory ─────────────────────────────────────────────────────────────
export function makeField(type) {
  const def = FIELD_TYPES[type];
  if (!def) return null;
  const data = JSON.parse(JSON.stringify(def.defaultData));
  if (type === 'selection_group' && Array.isArray(data.items)) {
    data.items = data.items.map(it => ({ ...it, id: uid() }));
  }
  if (type === 'column_group' && Array.isArray(data.columns)) {
    data.columns = data.columns.map(col => ({ ...col, id: uid(), fields: [] }));
  }
  return { id: uid(), ...data, _type: type };
}

// ── Column field factory (a field placed inside a column) ─────────────────────
export function makeColumnField(type) {
  return makeField(type); // same shape, just placed inside a column
}

// ── Selection group factory ───────────────────────────────────────────────────
export function makeSelectionGroup(title) {
  return {
    id: uid(),
    title: title || 'New Group',
    subtitle: '',
    description: '',
    badge: '',
    selectionMode: 'single',
    required: true,
    layout: 'grid',
    columns: 3,
    items: [
      { id: uid(), title: 'Option 1', description: '', icon: 'ConfirmationNumber', price: null, currency: 'PHP', priceLabel: 'Free',  available: true, order: 0 },
      { id: uid(), title: 'Option 2', description: '', icon: 'Stars',              price: null, currency: 'PHP', priceLabel: null,    available: true, order: 1 },
    ],
    order: 0,
  };
}

// ── Form settings factory (merges API data) ───────────────────────────────────
export function makeDefaultSettings(formData) {
  return {
    headerTitle:    formData?.headerTitle    || '',
    headerSubtitle: formData?.headerSubtitle || '',
    description:    formData?.headerTagline  || formData?.description || '',
    branding: {
      layout: 'card-left', mediaUrl: null, mediaType: 'image', mediaOverlayOpacity: 0.45,
      primaryColor: '#1565C0', backgroundColor: '#EEF2F7', cardColor: '#FFFFFF', textColor: '#1A2C42',
      ...(formData?.branding || {}),
    },
    successPage: {
      layout: 'image-bg', mediaUrl: null, mediaOverlayOpacity: 0.45,
      subtitle: 'Registration Complete', title: "You're registered!",
      message: "Thank you for signing up. We'll send you a confirmation email shortly.",
      showEventDetails: true, supportText: '',
      ctaPrimary: { label: '', url: '' }, ctaSecondary: { label: '', url: '' },
      ...(formData?.successPage || {}),
    },
  };
}

// ── Recursively normalise a field (handles nested column_group) ──────────────
const SYSTEM_FIELD_IDS = {
  '__sys_email__':    { _systemField: true, _systemKey: 'email' },
  '__sys_password__': { _systemField: true, _systemKey: 'password' },
  '__sys_empnum__':   { _systemField: true, _systemKey: 'employeeNumber' },
};
// Also match by _systemKey stored in the API response
const SYSTEM_KEYS = new Set(['email', 'password', 'employeeNumber', 'magicLink']);

function normaliseField(f) {
  const base = { ...f, id: f.id || uid(), _type: f.type };
  // Re-attach system markers (they're stripped before saving, restored on load)
  if (SYSTEM_FIELD_IDS[f.id]) Object.assign(base, SYSTEM_FIELD_IDS[f.id]);
  else if (f._systemKey && SYSTEM_KEYS.has(f._systemKey)) base._systemField = true;
  if (f.type === 'selection_group' && Array.isArray(f.items)) {
    base.items = f.items.map(it => ({ ...it, id: it.id || uid() }));
  }
  if (f.type === 'column_group') {
    const apiCols = f.layoutColumns || f.columns || [];
    base.columns = apiCols.map(col => ({
      id:     col.id || uid(),
      fields: (col.fields || []).map(cf => normaliseField(cf)),
    }));
    delete base.layoutColumns;
  }
  return base;
}

// ── API response normaliser — maps server step shape → builder step shape ─────
export function normaliseSteps(rawSteps) {
  return rawSteps
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((step, idx) => {
      const isSelection = step.type === 'selection';

      const flatFields = (step.sections || [])
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .flatMap(section =>
          (section.fields || [])
            .slice()
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map(f => normaliseField(f))
        );

      const rawFields = (step.fields || []).map(f => ({ ...f, id: f.id || uid(), _type: f.type }));
      const allFields = flatFields.length > 0 ? flatFields : rawFields;

      const selectionGroups = (step.selectionGroups || [])
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map(g => ({ ...g, id: g.id || uid(), items: (g.items || []).map(it => ({ ...it, id: it.id || uid() })) }));

      return {
        _id:             step._id ? String(step._id) : (step.id || uid()),
        _originalId:     step.id,
        _isNew:          !step._id && !step.id,
        stepType:        isSelection ? 'selection' : 'fields',
        title:           step.label || step.title || `Step ${idx + 1}`,
        order:           step.order ?? idx,
        fields:          isSelection ? [] : allFields,
        sections:        step.sections || [],
        selectionGroups: isSelection ? selectionGroups : [],
        history:         [isSelection ? [] : allFields],
        histIdx:         0,
      };
    });
}

// ── Recursively serialise a field for the API ────────────────────────────────
function serialiseField({ _type, _systemField, _systemKey, id, ...rest }) {
  const type = _type || rest.type;
  const base = { ...rest, id, type };
  if (type === 'selection_group') {
    base.items = rest.items || [];
  }
  if (type === 'column_group' && Array.isArray(rest.columns)) {
    base.layoutColumns = rest.columns.map(col => ({
      id:     col.id,
      fields: (col.fields || []).map(cf => serialiseField(cf)),
    }));
    delete base.columns;
  }
  return base;
}

// ── Builder step shape → API payload ─────────────────────────────────────────
export function serialiseSteps(steps) {
  return steps.map((s, idx) => ({
    ...(s._isNew ? {} : { _id: s._id }),
    id:    s._originalId || s._id,
    label: s.title,
    title: s.title,
    type:  s.stepType || 'fields',
    order: idx,
    sections: s.stepType === 'selection' ? [] : [
      {
        id:     'section-general',
        title:  'General Information',
        order:  0,
        fields: s.fields.map(f => serialiseField(f)),
      },
    ],
    selectionGroups: s.stepType === 'selection' ? s.selectionGroups : [],
    fields: [],
  }));
}